import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Tags } from 'lucide-react';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PCMP_CATEGORY_NAME_MAX_LENGTH } from '../../constants/pcmpProject.constants';
import { useCreatePcmpCategory } from '../../hooks/useCreatePcmpCategory';
import type { PcmpCatalogItem } from '../../types/pcmpProject.types';

const categoryFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Name is required')
    .max(PCMP_CATEGORY_NAME_MAX_LENGTH, `At most ${PCMP_CATEGORY_NAME_MAX_LENGTH} characters`),
});

type CategoryFormValues = z.infer<typeof categoryFormSchema>;

interface PcmpCategoryQuickAddModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (category: PcmpCatalogItem) => void;
}

/** Legacy CreateCategory: add a missing category without leaving the form. */
export function PcmpCategoryQuickAddModal({
  open,
  onOpenChange,
  onCreated,
}: PcmpCategoryQuickAddModalProps) {
  const form = useForm<CategoryFormValues>({
    resolver: zodResolver(categoryFormSchema),
    defaultValues: { name: '' },
  });
  const { mutate, isPending } = useCreatePcmpCategory((category) => {
    onCreated(category);
    onOpenChange(false);
  });

  useEffect(() => {
    if (open) form.reset({ name: '' });
  }, [open, form]);

  return (
    <Dialog open={open} onOpenChange={(next) => !isPending && onOpenChange(next)}>
      <DialogContent className="gap-0 sm:max-w-md">
        <DialogHeader className="-mx-4 -mt-4 flex-row items-center gap-3 border-b p-4 pr-12">
          <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
            <Tags className="size-4" />
          </div>
          <DialogTitle>New Category</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          {/* Its own form element: the dialog renders outside the project form. */}
          <form
            onSubmit={(event) => {
              event.stopPropagation();
              void form.handleSubmit((values) => mutate(values.name))(event);
            }}
            className="flex flex-col gap-6 pt-5"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Name <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="e.g., Science Test Paper"
                      autoComplete="off"
                      maxLength={PCMP_CATEGORY_NAME_MAX_LENGTH}
                      autoFocus
                    />
                  </FormControl>
                  <FormDescription>It is added as a row of this grid.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending} className="gap-1.5">
                {isPending ? <Loader2 className="size-4 animate-spin" /> : null}
                {isPending ? 'Adding...' : 'Add Category'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
